<template>
  <!--
    UserList — the User_List view.

    Renders every account in a Vuetify v-data-table with a role chip, an inline
    admin/employee toggle, and edit/delete actions. Mirrors the structure and
    conventions of ProjectList.vue (same load/error/empty states, same optimistic
    update + rollback pattern, same "container owns the snackbars" split via
    emitted events) so the two admin screens behave identically.

    Search and role filtering are done client-side over the full fetched set,
    matching how the server returns all users in one call.

    The current user's own row is marked "You" and its role selector and delete
    control are disabled: the server refuses self-demotion and self-deletion, so
    disabling them here avoids offering an action that can only fail. The same
    applies to accounts ranked above the signed-in user, and to the last super
    administrator or last administrator. The server remains the authority — these
    are affordances, not the enforcement.
  -->
  <div class="user-list">
    <!-- Search / role filter / clear controls. -->
    <v-row class="user-list__controls" dense align="center">
      <v-col cols="12" md="5">
        <v-text-field
          v-model="search"
          label="Search name or email"
          color="#e7511e"
          prepend-inner-icon="mdi-magnify"
          density="comfortable"
          variant="outlined"
          hide-details
          clearable
          data-test="search"
        />
      </v-col>

      <v-col cols="7" md="4">
        <v-select
          v-model="roleFilter"
          :items="roleOptions"
          item-title="title"
          item-value="value"
          label="Role"
          color="#e7511e"
          density="comfortable"
          variant="outlined"
          hide-details
          data-test="role-filter"
        />
      </v-col>

      <v-col cols="5" md="3" class="d-flex justify-end">
        <v-btn
          variant="text"
          color="#e7511e"
          :disabled="!hasActiveCriteria"
          data-test="clear-criteria"
          @click="clearCriteria"
        >
          Clear filters
        </v-btn>
      </v-col>
    </v-row>

    <!-- Error / timeout state: message, zero rows, retry. -->
    <template v-if="loadError">
      <v-alert
        type="error"
        variant="tonal"
        class="my-4"
        data-test="load-error"
      >
        {{ loadError }}
      </v-alert>
      <div class="d-flex justify-center my-4">
        <v-btn color="#e7511e" data-test="retry" @click="retry">
          Retry
        </v-btn>
      </div>
    </template>

    <!-- Empty state: no accounts at all. In practice unreachable, since you
         must be signed in as an administrator to see this screen, but handled so
         the table never renders a bare frame. -->
    <template v-else-if="!loading && rawUsers.length === 0">
      <div class="user-list__empty text-center my-8" data-test="empty-state">
        <div class="text-h6 mb-2">No users yet</div>
        <div class="text-body-2 mb-4">
          Add an account so your team can sign in.
        </div>
        <v-btn color="#e7511e" data-test="add-first" @click="addUser">
          Add first user
        </v-btn>
      </div>
    </template>

    <template v-else>
      <v-data-table
        :headers="headers"
        :items="pageRows"
        :items-per-page="PAGE_SIZE"
        :loading="loading"
        item-value="id"
        class="user-list__table elevation-1"
        hide-default-footer
        data-test="user-table"
      >
        <!-- Name cell, with a "You" marker on the acting administrator's row. -->
        <template #[`item.displayName`]="{ item }">
          <span>{{ item.displayName }}</span>
          <v-chip
            v-if="item.isSelf"
            size="x-small"
            variant="flat"
            color="#41281b"
            class="ml-2"
            data-test="self-chip"
          >
            You
          </v-chip>
        </template>

        <!-- Role chip. -->
        <template #[`item.role`]="{ item }">
          <v-chip
            :color="roleChipColor(item.role)"
            size="small"
            variant="flat"
            data-test="role-chip"
          >
            {{ item.roleLabel }}
          </v-chip>
        </template>

        <!--
          Inline role selector with optimistic update + rollback.

          A select rather than a switch, because there are now three roles. The
          options are limited to the roles the signed-in user may actually assign
          (from the server's `assignableRoles`), and the control is disabled where
          the server would refuse the change anyway: your own row, an account
          ranked above you, the last super administrator, and the last account
          holding administrator access.
        -->
        <template #[`item.roleSelect`]="{ item }">
          <v-select
            :model-value="item.role"
            :items="roleOptionsFor(item)"
            item-title="title"
            item-value="value"
            :disabled="loading || pendingId != null || !canChangeRole(item)"
            :loading="pendingId === item.id && pendingAction === 'role'"
            density="compact"
            variant="outlined"
            hide-details
            class="user-list__role-select"
            data-test="role-select"
            @update:model-value="(next) => changeRole(item, next)"
          />
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="d-flex ga-1 justify-end align-center">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="#e7511e"
              :disabled="loading"
              data-test="edit"
              @click="editUser(item)"
            />
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              :disabled="loading || pendingId != null || !canDelete(item)"
              data-test="delete"
              @click="requestDelete(item)"
            />
          </div>
        </template>

        <template #no-data>
          <div
            v-if="!loading"
            class="user-list__no-results text-center my-6"
            data-test="empty-result"
          >
            <div class="text-body-1 mb-3">
              No users match the current search and filters.
            </div>
            <v-btn
              variant="text"
              color="#e7511e"
              :disabled="!hasActiveCriteria"
              data-test="clear-criteria-inline"
              @click="clearCriteria"
            >
              Clear filters
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <div
        v-if="pageCount > 1"
        class="d-flex justify-center mt-4"
      >
        <v-pagination
          v-model="page"
          :length="pageCount"
          color="#e7511e"
          density="comfortable"
          data-test="pagination"
        />
      </div>
    </template>

    <!-- Delete confirmation. Names the account so an administrator cannot
         remove the wrong person with a mis-click. -->
    <v-dialog
      v-model="deleteDialog"
      max-width="480"
      data-test="delete-dialog"
    >
      <v-card>
        <v-card-title class="text-h6">Delete user</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong data-test="delete-dialog-title">{{ deleteTargetLabel }}</strong>?
          They will lose access immediately. This cannot be undone.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            variant="text"
            :disabled="pendingAction === 'delete'"
            data-test="delete-cancel"
            @click="cancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="pendingAction === 'delete'"
            data-test="delete-confirm"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// UserList — User_List view for the Admin vs Employee role management screen.
import { mapStores } from 'pinia'
import AdminUserApi from 'services/AdminUserApi'
import { useAuthStore } from '@/stores/auth'
import {
  PAGE_SIZE,
  ALL,
  ROLE_SUPERADMIN,
  ROLE_ADMIN,
  ROLE_EMPLOYEE,
  rankOf,
  hasAtLeast,
  labelForRole,
  deriveUserRows,
  filterUserRows,
  paginateUsers,
  countAdmins,
  countSuperAdmins
} from 'services/userListDerivation'

// The list request must fail rather than hang if the server never responds.
// Mirrors the 30s deadline ProjectList applies to its own load.
const LOAD_TIMEOUT_MS = 30000

// Best-effort extraction of a human-readable message from an API error while
// never leaking internal detail (the server sends generic messages).
function extractErrorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data.message === 'string' && data.message) return data.message
    if (typeof data.error === 'string' && data.error) return data.error
    // The admin endpoints report validation refusals (including the
    // self-demotion / last-administrator guards) as { fields: { <field>: msg } }.
    // Surface the first of those so the administrator sees WHY it was refused
    // rather than a generic failure.
    if (data.fields && typeof data.fields === 'object') {
      const first = Object.keys(data.fields)[0]
      if (first && typeof data.fields[first] === 'string') return data.fields[first]
    }
  }
  return fallback
}

export default {
  name: 'UserList',

  emits: ['add', 'edit', 'loaded', 'role-changed', 'deleted', 'error'],

  data () {
    return {
      PAGE_SIZE,

      // Raw managed-user payloads from the Admin_User_Api.
      rawUsers: [],

      loading: false,
      loadError: '',

      search: '',
      roleFilter: ALL,

      page: 1,

      // Optimistic-action tracking: `pendingId` is the account a request is in
      // flight for and `pendingAction` names it, so the right control spins and
      // its siblings disable.
      pendingId: null,
      pendingAction: null,

      deleteDialog: false,
      deleteTarget: null,

      // The roles the signed-in user may assign, as reported by the server on
      // load. Defaults to none until the list responds, so the selector never
      // offers a change the server would refuse.
      assignableRoles: [],

      roleOptions: [
        { title: 'All roles', value: ALL },
        { title: labelForRole(ROLE_SUPERADMIN), value: ROLE_SUPERADMIN },
        { title: labelForRole(ROLE_ADMIN), value: ROLE_ADMIN },
        { title: labelForRole(ROLE_EMPLOYEE), value: ROLE_EMPLOYEE }
      ]
    }
  },

  computed: {
    ...mapStores(useAuthStore),

    headers () {
      return [
        { title: 'Name', key: 'displayName', sortable: true },
        { title: 'Email', key: 'email', sortable: true },
        { title: 'Role', key: 'role', sortable: true },
        { title: 'Change role', key: 'roleSelect', sortable: false, align: 'center' },
        { title: 'Actions', key: 'actions', sortable: false, align: 'end' }
      ]
    },

    // The signed-in administrator's own public_id, used to mark their row and
    // disable the actions the server would refuse. public_id is used rather than
    // the numeric id because it is what the login response persists.
    currentPublicId () {
      const profile = this.authStore.profile
      return (profile && profile.public_id) || null
    },

    // The signed-in user's own role, used for the rank comparisons that decide
    // which rows they may act on.
    actingRole () {
      return this.authStore.role
    },

    actingRank () {
      return rankOf(this.actingRole)
    },

    // All rows derived from the raw payloads, with the self marker applied.
    allRows () {
      return deriveUserRows(this.rawUsers, this.currentPublicId)
    },

    // How many accounts hold administrator access (admin or superadmin) in the
    // fetched set. Used to disable the demote/delete controls on the last one.
    adminCount () {
      return countAdmins(this.rawUsers)
    },

    // How many super administrators exist, so the last one cannot be demoted or
    // deleted from the UI either.
    superAdminCount () {
      return countSuperAdmins(this.rawUsers)
    },

    filteredRows () {
      return filterUserRows(this.allRows, {
        search: this.search || '',
        role: this.roleFilter
      })
    },

    pages () {
      return paginateUsers(this.filteredRows, PAGE_SIZE)
    },

    pageCount () {
      return this.pages.length
    },

    pageRows () {
      if (this.pages.length === 0) return []
      const index = Math.min(this.page, this.pages.length) - 1
      return this.pages[index] || []
    },

    hasActiveCriteria () {
      return (this.search || '').trim() !== '' || this.roleFilter !== ALL
    },

    deleteTargetLabel () {
      const target = this.deleteTarget
      if (!target) return 'this user'
      return target.displayName || target.email || 'this user'
    }
  },

  watch: {
    search () {
      this.page = 1
    },
    roleFilter () {
      this.page = 1
    },
    pageCount (count) {
      if (this.page > count) {
        this.page = count > 0 ? count : 1
      }
    }
  },

  created () {
    this.loadUsers()
  },

  methods: {
    async loadUsers () {
      this.loading = true
      this.loadError = ''

      let timer
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          reject(new Error('timeout'))
        }, LOAD_TIMEOUT_MS)
      })

      try {
        const res = await Promise.race([AdminUserApi.list(), timeout])
        const data = (res && res.data) || {}
        const users = data.users || []
        this.rawUsers = Array.isArray(users) ? users : []
        // Follow the server's answer on what this user may assign rather than
        // recomputing it here, so the two can never disagree.
        this.assignableRoles = Array.isArray(data.assignableRoles)
          ? data.assignableRoles
          : []
        this.page = 1
        this.$emit('loaded', this.rawUsers)
      } catch (err) {
        this.rawUsers = []
        this.loadError =
          err && err.message === 'timeout'
            ? 'The user list took too long to load. Please try again.'
            : extractErrorMessage(err, 'Unable to load users. Please try again.')
      } finally {
        clearTimeout(timer)
        this.loading = false
      }
    },

    retry () {
      this.loadUsers()
    },

    clearCriteria () {
      this.search = ''
      this.roleFilter = ALL
      this.page = 1
    },

    // A role change is offered only where the server would accept it:
    //   - not on your own row (self-demotion is refused)
    //   - not on an account ranked above you (an admin cannot touch a superadmin)
    //   - not when it would empty the superadmin tier
    //   - not when it would leave nobody with administrator access
    //   - and only if you can assign any role at all
    // These mirror the server's guards; the server still enforces them.
    canChangeRole (row) {
      if (!row) return false
      if (row.isSelf) return false
      if (row.roleRank > this.actingRank) return false
      if (row.isSuperAdmin && this.superAdminCount <= 1) return false
      if (row.isAdmin && this.adminCount <= 1) return false
      return this.assignableRoles.length > 0
    },

    // Deletion is offered under the same rank and last-of-tier rules.
    canDelete (row) {
      if (!row) return false
      if (row.isSelf) return false
      if (row.roleRank > this.actingRank) return false
      if (row.isSuperAdmin && this.superAdminCount <= 1) return false
      if (row.isAdmin && this.adminCount <= 1) return false
      return true
    },

    // Options for a row's role selector: everything the signed-in user may
    // assign, plus the row's CURRENT role so the select always has a value to
    // display even when that role is above what they could assign.
    roleOptionsFor (row) {
      const values = this.assignableRoles.slice()
      if (row && row.role && values.indexOf(row.role) === -1) {
        values.push(row.role)
      }
      return values
        .slice()
        .sort((a, b) => rankOf(a) - rankOf(b))
        .map((value) => ({ title: labelForRole(value), value }))
    },

    roleChipColor (role) {
      if (role === ROLE_SUPERADMIN) return '#41281b'
      if (role === ROLE_ADMIN) return 'primary'
      return 'grey'
    },

    addUser () {
      this.$emit('add')
    },

    editUser (row) {
      if (row && row.id != null) {
        this.$emit('edit', row.id)
      }
    },

    // Change an account's role, applying the change optimistically and rolling
    // back if the request fails.
    async changeRole (row, nextRole) {
      if (!row || row.id == null || this.pendingId != null) return
      if (!this.canChangeRole(row)) return
      // The select fires on every assignment; ignore a no-op so re-picking the
      // current role does not issue a pointless request.
      if (!nextRole || nextRole === row.role) return

      const id = row.id
      const snapshot = this.rawUsers.map(u => ({ ...u }))

      this.rawUsers = this.rawUsers.map(u =>
        u.id === id
          ? {
            ...u,
            role: nextRole,
            // Keep the mirrored flags consistent with the optimistic role so the
            // derived row (and the last-of-tier counts) stay correct until the
            // server responds.
            is_admin: hasAtLeast(nextRole, ROLE_ADMIN),
            is_super_admin: nextRole === ROLE_SUPERADMIN
          }
          : u
      )

      this.pendingId = id
      this.pendingAction = 'role'

      try {
        await AdminUserApi.setRole(id, nextRole)
        this.$emit('role-changed', {
          id,
          role: nextRole,
          message: `${row.displayName} is now ${labelForRole(nextRole).toLowerCase()}.`
        })
      } catch (err) {
        // Restore the previously displayed role.
        this.rawUsers = snapshot
        this.$emit('error', {
          message: extractErrorMessage(
            err,
            'Unable to change the user role. Please try again.'
          )
        })
      } finally {
        this.pendingId = null
        this.pendingAction = null
      }
    },

    requestDelete (row) {
      if (!row || row.id == null) return
      if (!this.canDelete(row)) return
      this.deleteTarget = { id: row.id, displayName: row.displayName, email: row.email }
      this.deleteDialog = true
    },

    cancelDelete () {
      this.deleteDialog = false
      this.deleteTarget = null
    },

    async confirmDelete () {
      if (!this.deleteTarget || this.deleteTarget.id == null) return

      const { id, displayName, email } = this.deleteTarget
      this.pendingId = id
      this.pendingAction = 'delete'

      try {
        await AdminUserApi.remove(id)
        this.rawUsers = this.rawUsers.filter(u => u.id !== id)
        this.deleteDialog = false
        this.deleteTarget = null
        this.$emit('deleted', {
          id,
          message: `${displayName || email} was deleted.`
        })
      } catch (err) {
        // Retain the account in the list on failure.
        this.$emit('error', {
          message: extractErrorMessage(
            err,
            'Unable to delete the user. Please try again.'
          )
        })
      } finally {
        this.pendingId = null
        this.pendingAction = null
      }
    }
  }
}
</script>

<style scoped>
.user-list__controls {
  margin-bottom: 8px;
}

.user-list__empty,
.user-list__no-results {
  color: #41281b;
}

/* Keep the inline role selector compact so the table row height is unchanged. */
.user-list__role-select {
  min-width: 170px;
  max-width: 210px;
  margin: 0 auto;
}
</style>
